import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    HStack,
    Icon,
    Input,
    Kbd,
    Modal,
    ModalContent,
    ModalOverlay,
    Spinner,
    Text,
    VStack,
    Wrap,
    WrapItem,
    useDisclosure,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import {
    TbPackage,
    TbCar,
    TbHistory,
    TbSparkles,
    TbArrowBack,
    TbFileExport,
    TbFileText,
    TbBuildingWarehouse,
    TbMessageCircle,
    TbChartBar,
    TbSettings,
    TbArrowRight,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useSearch, EMSReponse, OrderReponse } from '../hooks/useSearch';
import { useRecentActivity } from '../hooks/useRecentActivity';
import { useAskCarlThreadPreviews } from '../hooks/useAskCarlThreadPreviews';
import { AskCarlThread } from '../types/askCarl';
import EMSDetailModal from './EMSDetailModal';
import CarlLogo from '../components/askCarl/CarlLogo';
import { useIsOverflowing } from '../hooks/useIsOverflowing';
import SessionContext from '../contexts/SessionContext';
import { canOpenReporting } from '../utils/inventoryReportingAccess';
import OrganizationSwitcherPanel from '../components/OrganizationSwitcherPanel';
import { Button as YaxleButton, Icon as YaxleIcon } from '../ui';

type SearchResult = EMSReponse | OrderReponse;
type PaletteMode = 'search' | 'carl';

/** ENG-2186: scope bubbles under the search input. */
type SearchScope = 'all' | 'ordering' | 'exports' | 'invoicing' | 'inventory';

const SCOPE_LABELS: Record<SearchScope, string> = {
    all: 'All',
    ordering: 'Ordering',
    exports: 'Exports',
    invoicing: 'Invoicing',
    inventory: 'Inventory',
};

interface JumpToItem {
    key: string;
    label: string;
    icon: React.ElementType;
    path: string;
}

/** Unified idle-view rows so arrow keys / Enter span searches, jumps, and threads. */
type IdleItem =
    | { kind: 'search'; query: string }
    | { kind: 'jump'; item: JumpToItem }
    | { kind: 'thread'; thread: AskCarlThread };

const PARTBAY_BLUE = '#0071E3';
const COLLAPSED_RECENT_SEARCHES = 3;

/**
 * An org switch from the palette issues a new session and navigates, which can remount
 * the palette (e.g. leaving Ask Carl). The typed query is parked here so whichever
 * palette instance sees the new session can carry on searching in the new org.
 */
const PARKED_SEARCH_KEY = 'org_switch_parked_search';
const PARKED_SEARCH_TTL_MS = 15_000;

const parkSearchForOrgSwitch = (query: string) => {
    sessionStorage.setItem(PARKED_SEARCH_KEY, JSON.stringify({ query, at: Date.now() }));
};

const clearParkedSearch = () => sessionStorage.removeItem(PARKED_SEARCH_KEY);

/** Reads and clears the query parked by an org switch; null when there is none (or it's stale). */
export const takeParkedOrgSwitchSearch = (): string | null => {
    const raw = sessionStorage.getItem(PARKED_SEARCH_KEY);
    if (raw === null) return null;
    clearParkedSearch();
    try {
        const { query, at } = JSON.parse(raw) as { query?: unknown; at?: unknown };
        const isFresh = typeof at === 'number' && Date.now() - at < PARKED_SEARCH_TTL_MS;
        return isFresh && typeof query === 'string' ? query : null;
    } catch {
        return null;
    }
};

interface CommandSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Whether the Ask Carl mode is available to this user. */
    canAskCarl: boolean;
    modifierLabel: 'Cmd' | 'Ctrl';
    /** Query to open with (a search carried over from an org switch). */
    initialQuery?: string;
}

/**
 * Notion-style command palette (Cmd/Ctrl+K): a centered container with the
 * platform search plus an "Ask Carl" toggle that trumps normal search —
 * when active, Enter starts a new Carl thread from the typed question.
 */
const CommandSearchModal = ({ isOpen, onClose, canAskCarl, modifierLabel, initialQuery }: CommandSearchModalProps) => {
    const navigate = useNavigate();
    const sessionContext = useContext(SessionContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement | null>(null);

    const [mode, setMode] = useState<PaletteMode>('search');
    const [scope, setScope] = useState<SearchScope>('all');
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedEmsId, setSelectedEmsId] = useState('');
    const [searchesExpanded, setSearchesExpanded] = useState(false);
    /** Employees only: the org bubble's picker, shown under "Search in…". */
    const [isOrgPickerOpen, setIsOrgPickerOpen] = useState(false);
    const isInternal = sessionContext?.isAuthenticated === true && sessionContext.isInternal;
    const emsModal = useDisclosure();
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);
    const setScrollContainerRef = useCallback((el: HTMLDivElement | null) => {
        listRef.current = el;
        bodyRef(el);
    }, [bodyRef]);

    const { activity, trackSearch, trackView, clearSearches } = useRecentActivity();
    const allRecentSearches = (activity.searches ?? []).slice(0, 8);
    // ENG-2186: show the top 3 with the option to expand.
    const recentSearches = searchesExpanded
        ? allRecentSearches
        : allRecentSearches.slice(0, COLLAPSED_RECENT_SEARCHES);
    const canExpandSearches = allRecentSearches.length > COLLAPSED_RECENT_SEARCHES;
    const recentThreads = useAskCarlThreadPreviews(isOpen, 5);
    // Only surface threads to users who can actually open Ask Carl.
    const showThreads = canAskCarl && recentThreads.length > 0;

    const isCarlMode = mode === 'carl' && canAskCarl;
    const showSearchResults = !isCarlMode && query.trim().length >= 2;

    /** ENG-2186 "Jump to": one row per reachable tab, gated by session tabs. */
    const jumpToItems = useMemo<JumpToItem[]>(() => {
        const tabs = sessionContext?.tabs ?? [];
        const items: JumpToItem[] = [];
        if (tabs.includes('dashboard')) items.push({ key: 'orders', label: 'Orders', icon: TbPackage as React.ElementType, path: '/dashboard' });
        if (tabs.includes('actvity')) items.push({ key: 'exports', label: 'Exports', icon: TbFileExport as React.ElementType, path: '/activity' });
        if (canAskCarl) items.push({ key: 'carl', label: 'Carl', icon: TbSparkles as React.ElementType, path: '/ask-carl' });
        if (tabs.includes('invoicing')) items.push({ key: 'invoicing', label: 'Invoicing', icon: TbFileText as React.ElementType, path: '/invoices' });
        if (tabs.includes('inventory')) items.push({ key: 'inventory', label: 'Inventory', icon: TbBuildingWarehouse as React.ElementType, path: '/inventory' });
        items.push({ key: 'messaging', label: 'Messaging', icon: TbMessageCircle as React.ElementType, path: '/chat' });
        if (canOpenReporting(sessionContext)) items.push({ key: 'reporting', label: 'Reporting', icon: TbChartBar as React.ElementType, path: '/reporting' });
        items.push({ key: 'settings', label: 'Settings', icon: TbSettings as React.ElementType, path: '/settings' });
        return items;
    }, [sessionContext, canAskCarl]);

    /** Scope bubbles: only offer scopes the user can reach. */
    const availableScopes = useMemo<SearchScope[]>(() => {
        const tabs = sessionContext?.tabs ?? [];
        const scopes: SearchScope[] = ['all'];
        if (tabs.includes('dashboard')) scopes.push('ordering');
        if (tabs.includes('actvity')) scopes.push('exports');
        if (tabs.includes('invoicing')) scopes.push('invoicing');
        if (tabs.includes('inventory')) scopes.push('inventory');
        return scopes;
    }, [sessionContext?.tabs]);

    // Idle view (no query): searches, then jump-to rows, then recent Carl threads.
    const idleItems = useMemo<IdleItem[]>(() => {
        const items: IdleItem[] = recentSearches.map((s) => ({ kind: 'search', query: s.query }));
        jumpToItems.forEach((item) => items.push({ kind: 'jump', item }));
        if (showThreads) recentThreads.forEach((thread) => items.push({ kind: 'thread', thread }));
        return items;
    }, [recentSearches, jumpToItems, recentThreads, showThreads]);

    /** Invoicing / Inventory scopes have no palette results — Enter jumps to the tab. */
    const isNavigationScope = scope === 'invoicing' || scope === 'inventory';

    const { data, isLoading, error } = useSearch(
        debouncedQuery,
        !isCarlMode && !isNavigationScope && debouncedQuery.length >= 2,
    );
    const isDebouncing = showSearchResults && !isNavigationScope && debouncedQuery !== query;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 150);
        return () => clearTimeout(timer);
    }, [query]);

    // Reset per open
    useEffect(() => {
        if (isOpen) {
            setQuery(initialQuery ?? '');
            setDebouncedQuery(initialQuery ?? '');
            setMode('search');
            setScope('all');
            setSelectedIndex(0);
            setSearchesExpanded(false);
            setIsOrgPickerOpen(false);
        }
    }, [isOpen, initialQuery]);

    const results = useMemo(() => {
        if (!showSearchResults || isNavigationScope) return [];
        const raw = data?.results ?? [];
        if (scope === 'ordering') return raw.filter((r) => r.type === 'order');
        if (scope === 'exports') return raw.filter((r) => r.type === 'ems');
        return raw;
    }, [showSearchResults, isNavigationScope, data?.results, scope]);
    const navigableCount = isCarlMode ? 0 : (showSearchResults ? results.length : idleItems.length);

    const resultsKey = showSearchResults
        ? `${scope}:${results.map((r) => (r.type === 'order' ? r.orderId : r.emsId)).join(',')}`
        : idleItems
            .map((item) =>
                item.kind === 'search' ? `s:${item.query}` : item.kind === 'jump' ? `j:${item.item.key}` : `t:${item.thread.threadId}`,
            )
            .join(',');
    useEffect(() => {
        setSelectedIndex(0);
    }, [resultsKey, mode]);

    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
        el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    const handleResultClick = useCallback((result: SearchResult) => {
        if (query.trim().length >= 2) trackSearch(query);
        if (result.type === 'order') {
            const displayLabel = result.roNumber ? `Order • RO ${result.roNumber}` : `Order • ${result.vin}`;
            trackView({
                type: 'order',
                key: `order:${result.orderId}`,
                label: displayLabel,
                path: `/order/${result.orderId}`,
                orderId: result.orderId,
                meta: { vin: result.vin, roNumber: result.roNumber, customer: result.customer },
            });
            navigate(`/order/${result.orderId}`);
        } else {
            const displayLabel = result.roNumber ? `EMS • RO ${result.roNumber}` : `EMS • ${result.vin}`;
            trackView({
                type: 'ems',
                key: `ems:${result.emsId}`,
                label: displayLabel,
                emsId: result.emsId,
                meta: { vin: result.vin, roNumber: result.roNumber, customer: result.customer },
            });
            setSelectedEmsId(result.emsId);
            emsModal.onOpen();
        }
        onClose();
    }, [query, trackSearch, trackView, navigate, emsModal, onClose]);

    const submitToCarl = useCallback(() => {
        const prompt = query.trim();
        if (!prompt) return;
        onClose();
        navigate('/ask-carl', { state: { askCarlPrompt: prompt } });
    }, [query, navigate, onClose]);

    const openThread = useCallback((threadId: string) => {
        onClose();
        navigate('/ask-carl', { state: { askCarlThreadId: threadId } });
    }, [navigate, onClose]);

    const toggleMode = useCallback(() => {
        if (!canAskCarl) return;
        setMode((prev) => (prev === 'carl' ? 'search' : 'carl'));
        inputRef.current?.focus();
    }, [canAskCarl]);

    /** Enter in an Invoicing / Inventory scope jumps to that tab (its page owns search). */
    const openNavigationScope = useCallback(() => {
        if (query.trim().length >= 2) trackSearch(query);
        onClose();
        navigate(scope === 'invoicing' ? '/invoices' : '/inventory');
    }, [query, trackSearch, onClose, navigate, scope]);

    const handleSelectItem = useCallback(() => {
        if (showSearchResults) {
            const result = results[selectedIndex];
            if (result) handleResultClick(result);
        } else {
            const item = idleItems[selectedIndex];
            if (item?.kind === 'search') setQuery(item.query);
            else if (item?.kind === 'jump') {
                onClose();
                navigate(item.item.path);
            } else if (item?.kind === 'thread') openThread(item.thread.threadId);
        }
    }, [showSearchResults, results, idleItems, selectedIndex, handleResultClick, openThread, onClose, navigate]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' && canAskCarl) {
            e.preventDefault();
            toggleMode();
        } else if (isCarlMode) {
            if (e.key === 'Enter') {
                e.preventDefault();
                submitToCarl();
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev < navigableCount - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (isNavigationScope && query.trim().length >= 2) openNavigationScope();
            else if (navigableCount > 0) handleSelectItem();
            else if (query.trim().length >= 2) trackSearch(query);
        }
    };

    const kbdProps = {
        fontSize: 'xs',
        bg: 'gray.100',
        color: 'gray.500',
        borderColor: 'gray.300',
        px: 1.5,
        py: 0.5,
    } as const;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size="2xl"
                initialFocusRef={inputRef}
                scrollBehavior="inside"
            >
                {/* No blur/dim (ENG-2297): the palette floats above the visible page. */}
                <ModalOverlay bg="transparent" />
                <ModalContent
                    mt="14vh"
                    borderWidth="1px"
                    borderColor="gray.300"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="white"
                    boxShadow="none"
                >
                    {/* Input row */}
                    <HStack
                        px={4}
                        py={3}
                        spacing={3}
                        borderBottomWidth={bodyOverflows ? '1px' : '0'}
                        borderBottomStyle="solid"
                        borderColor="gray.200"
                        bg={isCarlMode ? 'rgba(0, 113, 227, 0.04)' : 'white'}
                        transition="background 0.2s"
                    >
                        {isCarlMode ? (
                            <Icon as={TbSparkles as React.ElementType} boxSize={5} color="#0071E3" mx="4px" />
                        ) : (
                            <SearchIcon color="gray.400" boxSize={4} mx="6px" />
                        )}
                        <Input
                            ref={inputRef}
                            variant="unstyled"
                            fontSize="md"
                            color="gray.900"
                            px={3}
                            py={2}
                            placeholder={isCarlMode
                                ? 'Ask Carl about orders, parts, margins, or anything else…'
                                : 'Search by VIN, RO, customer name, claim #, or vehicle…'}
                            _placeholder={{ color: 'gray.400' }}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {canAskCarl && (
                            <Button
                                size="sm"
                                flexShrink={0}
                                leftIcon={<Icon as={TbSparkles as React.ElementType} boxSize={4} />}
                                onClick={toggleMode}
                                bg={isCarlMode ? PARTBAY_BLUE : 'gray.100'}
                                color={isCarlMode ? 'white' : 'gray.600'}
                                _hover={{ bg: isCarlMode ? PARTBAY_BLUE : 'gray.200' }}
                                _active={{ bg: isCarlMode ? PARTBAY_BLUE : 'gray.200' }}
                                borderRadius="lg"
                                transition="all 0.15s"
                            >
                                Ask Carl
                            </Button>
                        )}
                    </HStack>

                    {/* ENG-2186: scope bubbles for filtering between tabs */}
                    {!isCarlMode && (
                        <Box px={4} pt={2.5} pb={2} borderBottomWidth="1px" borderColor="gray.200">
                            <Text fontSize="xs" color="gray.400" mb={1.5}>
                                Search in…
                            </Text>
                            <Wrap spacing={1.5}>
                                {isInternal && sessionContext?.organizationName && (
                                    <WrapItem>
                                        {/* Current org, selected by default; opens the org picker. */}
                                        <YaxleButton
                                            variant="solid"
                                            size="xs"
                                            h="26px"
                                            px={3}
                                            borderRadius="full"
                                            fontWeight="semibold"
                                            rightIcon={
                                                <YaxleIcon
                                                    name="chevronDown"
                                                    size="0.5rem"
                                                    transform={isOrgPickerOpen ? 'rotate(180deg)' : undefined}
                                                />
                                            }
                                            aria-expanded={isOrgPickerOpen}
                                            onClick={() => setIsOrgPickerOpen((open) => !open)}
                                        >
                                            {sessionContext.organizationName}
                                        </YaxleButton>
                                    </WrapItem>
                                )}
                                {availableScopes.map((s) => {
                                    const isActive = scope === s;
                                    return (
                                        <WrapItem key={s}>
                                            <Button
                                                size="xs"
                                                h="26px"
                                                px={3}
                                                borderRadius="full"
                                                fontWeight="semibold"
                                                bg={isActive ? PARTBAY_BLUE : 'gray.100'}
                                                color={isActive ? 'white' : 'gray.600'}
                                                _hover={{ bg: isActive ? PARTBAY_BLUE : 'gray.200' }}
                                                _active={{ bg: isActive ? PARTBAY_BLUE : 'gray.200' }}
                                                onClick={() => {
                                                    setScope(s);
                                                    inputRef.current?.focus();
                                                }}
                                            >
                                                {SCOPE_LABELS[s]}
                                            </Button>
                                        </WrapItem>
                                    );
                                })}
                            </Wrap>
                        </Box>
                    )}

                    {/* Picking an org switches the whole account (the page behind goes to that org's
                        landing page, since an open detail page belongs to the old org); the palette
                        stays open and the typed search re-runs there. */}
                    {!isCarlMode && isInternal && isOrgPickerOpen && (
                        <Box borderBottomWidth="1px" borderColor="border.subtle">
                            <OrganizationSwitcherPanel
                                listMaxH="200px"
                                onSwitchStart={() => parkSearchForOrgSwitch(query)}
                                onSwitchError={clearParkedSearch}
                                onSwitched={() => {
                                    setIsOrgPickerOpen(false);
                                    inputRef.current?.focus();
                                }}
                            />
                        </Box>
                    )}

                    {/* Body */}
                    <Box ref={setScrollContainerRef} maxH="46vh" overflowY="auto">
                        {isCarlMode ? (
                            <VStack spacing={4} py={10} px={6}>
                                <CarlLogo size="88px" welcome />
                                <VStack spacing={1}>
                                    <Text fontSize="md" fontWeight="semibold" color="gray.800">
                                        {query.trim() ? 'Press Enter to ask Carl' : 'Start a new thread with Carl'}
                                    </Text>
                                    <Text fontSize="sm" color="gray.500" textAlign="center" maxW="380px">
                                        {query.trim()
                                            ? `"${query.trim()}" opens as a new conversation on the Ask Carl page.`
                                            : 'Type a question about orders, parts, quoting, re-keys, or margins — Carl picks it up in a fresh conversation.'}
                                    </Text>
                                </VStack>
                            </VStack>
                        ) : isNavigationScope && query.trim().length >= 2 ? (
                            <Box p={5} textAlign="center">
                                <Text fontSize="sm" color="gray.600">
                                    Search {SCOPE_LABELS[scope]} for “{query.trim()}”
                                </Text>
                                <Button
                                    mt={3}
                                    size="sm"
                                    variant="outline"
                                    rightIcon={<Icon as={TbArrowRight as React.ElementType} boxSize={4} />}
                                    color={PARTBAY_BLUE}
                                    borderColor="gray.300"
                                    _hover={{ bg: 'gray.50' }}
                                    onClick={openNavigationScope}
                                >
                                    Open {SCOPE_LABELS[scope]}
                                </Button>
                            </Box>
                        ) : showSearchResults ? (
                            <>
                                {(isDebouncing || isLoading) && (
                                    <Box p={5} textAlign="center">
                                        <Spinner size="sm" color="gray.600" />
                                        <Text mt={2} fontSize="sm" color="gray.600">Searching...</Text>
                                    </Box>
                                )}

                                {error && (
                                    <Alert status="error" size="sm" borderRadius="xl" m={2}>
                                        <AlertIcon />
                                        Failed to search orders
                                    </Alert>
                                )}

                                {!isDebouncing && !isLoading && !error && results.length > 0 && (
                                    <VStack spacing={0} align="stretch" p={2}>
                                        {results.map((result, index) => {
                                            const isSelected = selectedIndex === index;
                                            return (
                                                <Box
                                                    key={result.type === 'order' ? result.orderId : result.emsId}
                                                    data-index={index}
                                                    p={3}
                                                    cursor="pointer"
                                                    bg={isSelected ? PARTBAY_BLUE : 'transparent'}
                                                    _hover={{ bg: isSelected ? PARTBAY_BLUE : 'gray.100' }}
                                                    borderRadius="lg"
                                                    onClick={() => handleResultClick(result)}
                                                    onMouseEnter={() => setSelectedIndex(index)}
                                                    transition="background 150ms ease"
                                                >
                                                    <VStack align="start" spacing={1}>
                                                        <HStack spacing={2}>
                                                            <Icon
                                                                as={(result.type === 'order' ? TbPackage : TbCar) as React.ElementType}
                                                                w="16px"
                                                                h="16px"
                                                                color={isSelected ? 'white' : 'gray.700'}
                                                            />
                                                            <Text fontWeight="medium" fontSize="sm" color={isSelected ? 'white' : 'gray.800'}>
                                                                {result.vin}
                                                            </Text>
                                                            <Badge
                                                                bg={result.type === 'order' ? 'gray.400' : 'green.500'}
                                                                color="white"
                                                                fontSize="xs"
                                                                borderRadius="full"
                                                            >
                                                                {result.type === 'order' ? 'Order' : 'EMS'}
                                                            </Badge>
                                                            {result.type === 'order' && result.archived && (
                                                                <Badge
                                                                    bg={isSelected ? 'whiteAlpha.300' : 'orange.100'}
                                                                    color={isSelected ? 'white' : 'orange.700'}
                                                                    fontSize="xs"
                                                                    borderRadius="full"
                                                                >
                                                                    Archived
                                                                </Badge>
                                                            )}
                                                        </HStack>
                                                        <Text fontSize="xs" color={isSelected ? 'whiteAlpha.700' : 'gray.500'}>
                                                            {result.vehicle}
                                                        </Text>
                                                        <Text fontSize="xs" color={isSelected ? 'whiteAlpha.700' : 'gray.500'}>
                                                            RO: {result.roNumber} • {result.location}{result.insurance ? ` • ${result.insurance}` : ''}
                                                        </Text>
                                                        <Text fontSize="xs" color={isSelected ? 'whiteAlpha.700' : 'gray.500'}>
                                                            Claim #: {result.claimNumber || '-'} • Customer: {result.customer}
                                                        </Text>
                                                    </VStack>
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                )}

                                {!isDebouncing && !isLoading && !error && results.length === 0 && debouncedQuery.length >= 2 && (
                                    <Box p={5} textAlign="center">
                                        <Text fontSize="sm" color="gray.500">No results found</Text>
                                        {canAskCarl && (
                                            <Button
                                                mt={3}
                                                size="sm"
                                                variant="outline"
                                                leftIcon={<Icon as={TbSparkles as React.ElementType} boxSize={4} />}
                                                color={PARTBAY_BLUE}
                                                borderColor="gray.300"
                                                _hover={{ bg: 'blue.50' }}
                                                onClick={() => setMode('carl')}
                                            >
                                                Ask Carl instead
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box p={3}>
                                <VStack align="stretch" spacing={4}>
                                    {/* Recent Searches — top 3 with expand / clear (ENG-2186) */}
                                    <VStack align="stretch" spacing={2}>
                                        <HStack justify="space-between" px={1}>
                                            <Text fontSize="xs" color="gray.500" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase">
                                                Recent Searches
                                            </Text>
                                            <HStack spacing={0}>
                                                {canExpandSearches && (
                                                    <Button
                                                        variant="ghost"
                                                        size="xs"
                                                        onClick={(e) => { e.stopPropagation(); setSearchesExpanded((v) => !v); }}
                                                        color="gray.600"
                                                        _hover={{ bg: 'gray.100' }}
                                                    >
                                                        {searchesExpanded ? 'Show less' : 'Show more'}
                                                    </Button>
                                                )}
                                                {recentSearches.length > 0 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="xs"
                                                        onClick={(e) => { e.stopPropagation(); clearSearches(); }}
                                                        color="gray.600"
                                                        _hover={{ bg: 'gray.100' }}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </HStack>
                                        </HStack>
                                        {recentSearches.length === 0 ? (
                                            <Box px={1} pb={1}>
                                                <Text fontSize="sm" color="gray.400">No recent searches yet</Text>
                                            </Box>
                                        ) : (
                                            <VStack spacing={0} align="stretch">
                                                {recentSearches.map((s, index) => {
                                                    const isSelected = selectedIndex === index;
                                                    return (
                                                        <Box
                                                            key={s.query}
                                                            data-index={index}
                                                            px={3}
                                                            py={2.5}
                                                            cursor="pointer"
                                                            bg={isSelected ? PARTBAY_BLUE : 'transparent'}
                                                            _hover={{ bg: isSelected ? PARTBAY_BLUE : 'gray.100' }}
                                                            borderRadius="lg"
                                                            onClick={() => setQuery(s.query)}
                                                            onMouseEnter={() => setSelectedIndex(index)}
                                                            transition="background 150ms ease"
                                                        >
                                                            <HStack spacing={3}>
                                                                <Icon as={TbHistory as React.ElementType} w="18px" h="18px" color={isSelected ? 'white' : 'gray.500'} />
                                                                <Text fontSize="sm" color={isSelected ? 'white' : 'gray.800'} noOfLines={1}>
                                                                    {s.query}
                                                                </Text>
                                                            </HStack>
                                                        </Box>
                                                    );
                                                })}
                                            </VStack>
                                        )}
                                    </VStack>

                                    {/* Jump To — direct tab navigation (ENG-2186) */}
                                    {jumpToItems.length > 0 && (
                                        <VStack align="stretch" spacing={2}>
                                            <Text px={1} fontSize="xs" color="gray.500" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase">
                                                Jump To
                                            </Text>
                                            <VStack spacing={0} align="stretch">
                                                {jumpToItems.map((item, jumpIndex) => {
                                                    const index = recentSearches.length + jumpIndex;
                                                    const isSelected = selectedIndex === index;
                                                    return (
                                                        <Box
                                                            key={item.key}
                                                            data-index={index}
                                                            px={3}
                                                            py={2.5}
                                                            cursor="pointer"
                                                            bg={isSelected ? PARTBAY_BLUE : 'transparent'}
                                                            _hover={{ bg: isSelected ? PARTBAY_BLUE : 'gray.100' }}
                                                            borderRadius="lg"
                                                            onClick={() => {
                                                                onClose();
                                                                navigate(item.path);
                                                            }}
                                                            onMouseEnter={() => setSelectedIndex(index)}
                                                            transition="background 150ms ease"
                                                        >
                                                            <HStack spacing={3}>
                                                                <Icon as={item.icon} w="18px" h="18px" color={isSelected ? 'white' : 'gray.500'} />
                                                                <Text fontSize="sm" color={isSelected ? 'white' : 'gray.800'}>
                                                                    {item.label}
                                                                </Text>
                                                            </HStack>
                                                        </Box>
                                                    );
                                                })}
                                            </VStack>
                                        </VStack>
                                    )}

                                    {/* Recent Carl Threads */}
                                    {showThreads && (
                                        <VStack align="stretch" spacing={2}>
                                            <Text px={1} fontSize="xs" color="gray.500" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase">
                                                Recent Carl Chats
                                            </Text>
                                            <VStack spacing={0} align="stretch">
                                                {recentThreads.map((thread, threadIndex) => {
                                                    const index = recentSearches.length + jumpToItems.length + threadIndex;
                                                    const isSelected = selectedIndex === index;
                                                    return (
                                                        <Box
                                                            key={thread.threadId}
                                                            data-index={index}
                                                            px={3}
                                                            py={2.5}
                                                            cursor="pointer"
                                                            bg={isSelected ? PARTBAY_BLUE : 'transparent'}
                                                            _hover={{ bg: isSelected ? PARTBAY_BLUE : 'gray.100' }}
                                                            borderRadius="lg"
                                                            onClick={() => openThread(thread.threadId)}
                                                            onMouseEnter={() => setSelectedIndex(index)}
                                                            transition="background 150ms ease"
                                                        >
                                                            <HStack spacing={3}>
                                                                <Icon as={TbSparkles as React.ElementType} w="18px" h="18px" color={isSelected ? 'white' : '#0071E3'} />
                                                                <Text fontSize="sm" color={isSelected ? 'white' : 'gray.800'} noOfLines={1} flex="1">
                                                                    {thread.title}
                                                                </Text>
                                                                {thread.updatedAt && (
                                                                    <Text fontSize="xs" color={isSelected ? 'whiteAlpha.800' : 'gray.400'} flexShrink={0}>
                                                                        {moment(thread.updatedAt).fromNow(true)}
                                                                    </Text>
                                                                )}
                                                            </HStack>
                                                        </Box>
                                                    );
                                                })}
                                            </VStack>
                                        </VStack>
                                    )}
                                </VStack>
                            </Box>
                        )}
                    </Box>

                    {/* Footer: keyboard hints */}
                    <HStack
                        borderTopWidth="1px"
                        borderColor="gray.200"
                        bg="gray.50"
                        px={4}
                        py={2.5}
                        spacing={5}
                        justify="center"
                    >
                        {isCarlMode ? (
                            <HStack spacing={1}>
                                <Kbd {...kbdProps}><Icon as={TbArrowBack as React.ElementType} boxSize={3} mb="-2px" /></Kbd>
                                <Text fontSize="xs" color="gray.500" ml={1}>Ask Carl</Text>
                            </HStack>
                        ) : (
                            <>
                                <HStack spacing={1}>
                                    <Kbd {...kbdProps}>↑</Kbd>
                                    <Kbd {...kbdProps}>↓</Kbd>
                                    <Text fontSize="xs" color="gray.500" ml={1}>Navigate</Text>
                                </HStack>
                                <HStack spacing={1}>
                                    <Kbd {...kbdProps}>Enter</Kbd>
                                    <Text fontSize="xs" color="gray.500" ml={1}>Select</Text>
                                </HStack>
                            </>
                        )}
                        {canAskCarl && (
                            <HStack spacing={1}>
                                <Kbd {...kbdProps}>Tab</Kbd>
                                <Text fontSize="xs" color="gray.500" ml={1}>
                                    {isCarlMode ? 'Back to search' : 'Ask Carl'}
                                </Text>
                            </HStack>
                        )}
                        <HStack spacing={1}>
                            <Kbd {...kbdProps}>ESC</Kbd>
                            <Text fontSize="xs" color="gray.500" ml={1}>Close</Text>
                        </HStack>
                        <HStack spacing={1}>
                            <Kbd {...kbdProps}>{modifierLabel}</Kbd>
                            <Kbd {...kbdProps}>K</Kbd>
                            <Text fontSize="xs" color="gray.500" ml={1}>Toggle</Text>
                        </HStack>
                    </HStack>
                </ModalContent>
            </Modal>

            <EMSDetailModal
                isOpen={emsModal.isOpen}
                onClose={emsModal.onClose}
                emsId={selectedEmsId}
            />
        </>
    );
};

export default CommandSearchModal;
